import React, { useState, useCallback, useMemo } from 'react';
import { DatePicker, Select } from 'antd';
import { useSelector, useDispatch } from "react-redux";
import { changePeriod } from '../stateManagement/actions/report';
import { MomentDateUtils, SimpleDateUtils } from '../utils/dates';
import { DATE_FORMAT, PERIOD_MACROS } from '../utils/constants';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Extract predefined date ranges to constants
const PREDEFINED_RANGES = {
  [PERIOD_MACROS.LAST_7_DAYS]: () => MomentDateUtils.getLast7DaysDates(),
  [PERIOD_MACROS.LAST_MONTH]: () => MomentDateUtils.getLastFullMonthDates(),
  [PERIOD_MACROS.CURRENT_MONTH]: () => MomentDateUtils.getThisMonthDates(),
};

const PeriodSelector = React.memo(() => {
  const dispatch = useDispatch();
  const globalPeriod = useSelector((state) => state.reportReducer.period);
  
  // Use local state with default to global period
  const [localPeriod, setLocalPeriod] = useState(globalPeriod);

  // Memoized option rendering to prevent unnecessary re-renders
  const periodOptions = useMemo(() => [
    { value: PERIOD_MACROS.LAST_7_DAYS, label: 'Last 7 days' },
    { value: PERIOD_MACROS.CURRENT_MONTH, label: 'This month' },
    { value: PERIOD_MACROS.LAST_MONTH, label: 'Last month' },
    { value: 'custom', label: 'Custom' },
  ], []);

  // Memoized handler for period selection
  const handlePeriodChange = useCallback((value) => {
    setLocalPeriod(value);
    if (value !== 'custom') {
      dispatch(changePeriod(value));
    }
  }, [dispatch]);

  // Memoized handler for custom range selection
  const handleRangeChange = useCallback((value) => {
    if (value && value.length === 2) {
      const formattedPeriod = `${SimpleDateUtils.getDateFromMoment(value[0])}to${SimpleDateUtils.getDateFromMoment(value[1])}`;
      dispatch(changePeriod(formattedPeriod));
    }
  }, [dispatch]);

  // Render appropriate date picker based on selected period
  const renderDatePicker = useCallback(() => {
    switch (localPeriod) {
      case PERIOD_MACROS.LAST_7_DAYS:
      case PERIOD_MACROS.LAST_MONTH:
      case PERIOD_MACROS.CURRENT_MONTH:
        return (
          <RangePicker 
            value={PREDEFINED_RANGES[localPeriod]()} 
            format={DATE_FORMAT} 
            disabled 
          />
        );
      case 'custom':
        return (
          <RangePicker 
            format={DATE_FORMAT} 
            onChange={handleRangeChange} 
          />
        );
      default:
        return null;
    }
  }, [localPeriod, handleRangeChange]);

  return (
    <>
      <Select value={localPeriod} onChange={handlePeriodChange}>
        {periodOptions.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
      {renderDatePicker()}
    </>
  );
});

export default PeriodSelector;